import {
  ApartmentOutlined, ArrowRightOutlined, BankOutlined, CheckCircleFilled,
  LockOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Button, Card, Progress, Result, Tag, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';

const setupGroups = [
  {
    title: 'Organization foundation',
    description: 'Build the structure used by workforce planning.',
    items: [
      { icon: <BankOutlined />, title: 'Organization Profile', description: 'Add organization information and choose the number of levels.', buttonLabel: 'Start Organization Setup' },
      { icon: <SafetyCertificateOutlined />, title: 'Structure Levels', description: 'Name each level in the organization hierarchy.', buttonLabel: 'Configure Structure Levels' },
    ],
  },
  {
    title: 'Organization data',
    description: 'Fill each level using the names you configured.',
    items: [
      { icon: <ApartmentOutlined />, title: 'Level Data', description: 'Add data and connect every level to its parent.', buttonLabel: 'Continue Organization Data' },
    ],
  },
  {
    title: 'Review and complete',
    description: 'Validate the hierarchy before opening live insights.',
    items: [
      { icon: <SafetyCertificateOutlined />, title: 'Review Structure', description: 'Review the organization hierarchy.', buttonLabel: 'Review Organization Structure' },
      { icon: <CheckCircleFilled />, title: 'Finish Setup', description: 'Confirm that the initial setup is complete.', buttonLabel: 'Review and Finish Setup' },
    ],
  },
];

const items = setupGroups.flatMap((group) => group.items);
const humanize = (value, fallback) => value
  ? String(value).toLowerCase().split(/[_-]/).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
  : fallback;

export default function WorkspaceSetupCard({ workspace }) {
  const navigate = useNavigate();
  const usesCurrentFlow = workspace?.workspaceSetupFlowVersion === 2;
  const completedSteps = usesCurrentFlow
    ? Math.min(Number(workspace?.workspaceSetupStep || 0), items.length)
    : 0;
  const setupComplete = usesCurrentFlow && workspace?.workspaceSetupStatus === 'COMPLETE';
  const nextStepIndex = Math.min(completedSteps, items.length - 1);
  const nextStep = items[nextStepIndex];
  const completionPercent = Math.round((completedSteps / items.length) * 100);
  const remainingMinutes = Math.max(0, (items.length - completedSteps) * 3);

  const openStep = (index) => {
    let reviewStep = 5;
    try {
      const setupDraft = JSON.parse(sessionStorage.getItem(`workplansiq.organizationSetupDraft.${workspace?.id}`) || '{}');
      reviewStep = Number(setupDraft?.levelNames?.length || 3) + 2;
    } catch { /* Use the default three-level review step. */ }
    const wizardStepByChecklist = [0, 1, 2, reviewStep, reviewStep];
    navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.workspaceSetup}?step=${wizardStepByChecklist[index] ?? 0}`);
  };

  if (setupComplete) {
    return <Card className="workspace-setup-card"><Result status="success" title="Organization setup complete" subTitle="Your organization profile and initial structure are ready." /></Card>;
  }

  let itemOffset = 0;
  return (
    <Card className="workspace-setup-card">
      <div className="workspace-setup-card__header">
        <div><Tag color="blue">INITIAL SETUP</Tag><h2>Welcome to WorkPlansIQ</h2><p>Start with the essentials. You can explore the Dashboard and complete setup at your own pace.</p></div>
        <div className="workspace-setup-card__progress">
          <div><strong>{completedSteps} of {items.length} steps</strong><span>About {remainingMinutes} min remaining</span></div>
          <Progress percent={completionPercent} showInfo={false} strokeWidth={10} />
        </div>
      </div>

      <div className="workspace-setup-card__status-grid">
        <div><span>Selected plan</span><strong>{workspace?.plan || 'Not selected'}</strong></div>
        <div><span>Plan status</span><strong>{humanize(workspace?.subscriptionStatus, 'Draft')}</strong></div>
        <div><span>Organization</span><strong>{completedSteps > 0 ? workspace?.name || 'Created' : 'Not created'}</strong></div>
        <div><span>Billing</span><strong>{humanize(workspace?.paymentStatus, 'Not started')}</strong></div>
      </div>

      <div className="workspace-setup-card__groups">
        {setupGroups.map((group) => {
          const groupStart = itemOffset;
          itemOffset += group.items.length;
          return (
            <section className="workspace-setup-card__group" key={group.title}>
              <div className="workspace-setup-card__group-heading"><strong>{group.title}</strong><span>{group.description}</span></div>
              <div className="workspace-setup-card__steps">
                {group.items.map((item, groupIndex) => {
                  const index = groupStart + groupIndex;
                  const complete = index < completedSteps;
                  const active = index === completedSteps;
                  const accessible = complete || active;
                  const stepButton = (
                    <button type="button" className={`workspace-setup-card__step${active ? ' is-active' : ''}${complete ? ' is-complete' : ''}${accessible ? ' is-clickable' : ' is-locked'}`} disabled={!accessible} aria-label={`${complete ? 'Review or edit' : 'Continue'} ${item.title}`} onClick={() => openStep(index)}>
                      <span className="workspace-setup-card__step-icon">{complete ? <CheckCircleFilled /> : accessible ? item.icon : <LockOutlined />}</span>
                      <div><strong>{item.title}</strong><p>{item.description}</p></div>
                    </button>
                  );
                  return accessible
                    ? <div className="workspace-setup-card__step-wrapper" key={item.title}>{stepButton}</div>
                    : <Tooltip title={`Complete “${items[index - 1].title}” first.`} key={item.title}><div className="workspace-setup-card__step-wrapper">{stepButton}</div></Tooltip>;
                })}
              </div>
            </section>
          );
        })}
      </div>

      <div className="workspace-setup-card__footer">
        <div className="workspace-setup-card__next-step"><strong>Next: {nextStep.title}</strong><span>{nextStep.description} Progress is saved automatically; billing can be completed later.</span></div>
        <div className="workspace-setup-card__footer-actions">
          <Button className="workspace-setup-card__primary-action" type="primary" size="large" onClick={() => openStep(nextStepIndex)}>{nextStep.buttonLabel} <ArrowRightOutlined /></Button>
        </div>
      </div>
    </Card>
  );
}
